import React, { useEffect, useState } from 'react';
import { Card, Form, Input, Button, message, Divider, Space, Spin, Tag, Modal } from 'antd';
import { UserOutlined, MailOutlined, LockOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import api from '../../utils/api';
import { useDispatch } from 'react-redux';
import { logout } from '../../features/auth/authSlice';
import { useNavigate } from 'react-router-dom';

const Profile = () => {
  const [form] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [profileLoading, setProfileLoading] = useState(true);
  const [profileData, setProfileData] = useState(null);
  const [gaEnabled, setGaEnabled] = useState(false);
  const [gaLoading, setGaLoading] = useState(false);
  const [gaModalVisible, setGaModalVisible] = useState(false);
  const [gaInitData, setGaInitData] = useState(null);
  const [gaCode, setGaCode] = useState('');
  const [gaSubmitting, setGaSubmitting] = useState(false);
  const [gaDisableModalVisible, setGaDisableModalVisible] = useState(false);
  const [gaDisableCode, setGaDisableCode] = useState('');
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const fetchProfile = async () => {
    setProfileLoading(true);
    try {
      const response = await api.get('/admin/profile');
      if (response.code === 200) {
        setProfileData(response.data);
        form.setFieldsValue({
          realName: response.data.realName,
          email: response.data.email,
        });
      } else {
        message.error(response.message || '获取个人资料失败');
      }
    } catch (error) {
      message.error(error.message || '获取个人资料失败');
    } finally {
      setProfileLoading(false);
    }
  };

  const fetchGaStatus = async () => {
    try {
      const response = await api.get('/auth/admin/ga/status');
      if (response.code === 200) {
        setGaEnabled(response.data?.gaEnabled === true);
      }
    } catch (error) {
      console.error('获取Google验证器状态失败:', error);
    }
  };

  useEffect(() => {
    fetchProfile();
    fetchGaStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleProfileUpdate = async (values) => {
    setLoading(true);
    try {
      const response = await api.put('/admin/profile', {
        realName: values.realName,
        email: values.email,
      });

      if (response.code === 200) {
        message.success('个人资料更新成功');
        fetchProfile();
      } else {
        message.error(response.message || '更新失败');
      }
    } catch (error) {
      message.error(error.message || '更新失败');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordUpdate = async (values) => {
    setLoading(true);
    try {
      const response = await api.put('/admin/profile/password', {
        oldPassword: values.oldPassword,
        newPassword: values.newPassword,
        confirmPassword: values.confirmPassword,
      });

      if (response.code === 200) {
        message.success('密码修改成功，请重新登录');
        setTimeout(() => {
          dispatch(logout());
          navigate('/login');
        }, 1500);
      } else {
        message.error(response.message || '修改失败');
      }
    } catch (error) {
      message.error(error.message || '修改失败');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return '-';
    const d = new Date(date);
    return d.toLocaleString('zh-CN');
  };

  const handleGaInit = async () => {
    setGaLoading(true);
    try {
      const response = await api.post('/auth/admin/ga/init');
      if (response.code === 200) {
        setGaInitData(response.data);
        setGaCode('');
        setGaModalVisible(true);
      } else {
        message.error(response.message || '生成Google验证器失败');
      }
    } catch (error) {
      message.error(error.message || '生成Google验证器失败');
    } finally {
      setGaLoading(false);
    }
  };

  const handleGaVerify = async () => {
    if (!/^\d{6}$/.test(gaCode)) {
      message.warning('请输入6位动态验证码');
      return;
    }
    setGaSubmitting(true);
    try {
      const response = await api.post('/auth/admin/ga/verify', {
        secret: gaInitData.secret,
        gaCode,
      });
      if (response.code === 200) {
        message.success('Google验证器已启用');
        setGaModalVisible(false);
        setGaInitData(null);
        setGaCode('');
        setGaEnabled(true);
      } else {
        message.error(response.message || '启用失败');
      }
    } catch (error) {
      message.error(error.message || '启用失败');
    } finally {
      setGaSubmitting(false);
    }
  };

  const handleGaDisable = async () => {
    if (!/^\d{6}$/.test(gaDisableCode)) {
      message.warning('请输入6位动态验证码');
      return;
    }
    setGaSubmitting(true);
    try {
      const response = await api.post('/auth/admin/ga/disable', { gaCode: gaDisableCode });
      if (response.code === 200) {
        message.success('Google验证器已关闭');
        setGaDisableModalVisible(false);
        setGaDisableCode('');
        setGaEnabled(false);
      } else {
        message.error(response.message || '关闭失败');
      }
    } catch (error) {
      message.error(error.message || '关闭失败');
    } finally {
      setGaSubmitting(false);
    }
  };

  if (profileLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 800, margin: '0 auto' }}>
      <Card title="个人资料" style={{ marginBottom: 24 }}>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleProfileUpdate}
        >
          <Form.Item label="手机号">
            <Input prefix={<UserOutlined />} value={profileData?.phone} disabled />
          </Form.Item>
          <Form.Item
            name="realName"
            label="姓名"
            rules={[{ required: true, message: '请输入姓名' }]}
          >
            <Input prefix={<UserOutlined />} placeholder="请输入姓名" />
          </Form.Item>
          <Form.Item
            name="email"
            label="邮箱"
            rules={[
              { type: 'email', message: '邮箱格式不正确' }
            ]}
          >
            <Input prefix={<MailOutlined />} placeholder="请输入邮箱" />
          </Form.Item>
          <Form.Item label="角色">
            <Input value={profileData?.roleText} disabled />
          </Form.Item>
          <Form.Item label="创建时间">
            <Input value={formatDate(profileData?.createdAt)} disabled />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading}>
              保存修改
            </Button>
          </Form.Item>
        </Form>
      </Card>

      <Card title="修改密码">
        <Form
          form={passwordForm}
          layout="vertical"
          onFinish={handlePasswordUpdate}
        >
          <Form.Item
            name="oldPassword"
            label="原密码"
            rules={[
              { required: true, message: '请输入原密码' }
            ]}
          >
            <Input.Password prefix={<LockOutlined />} placeholder="请输入原密码" />
          </Form.Item>
          <Form.Item
            name="newPassword"
            label="新密码"
            rules={[
              { required: true, message: '请输入新密码' },
              { min: 6, message: '密码至少6位' }
            ]}
          >
            <Input.Password prefix={<LockOutlined />} placeholder="请输入新密码" />
          </Form.Item>
          <Form.Item
            name="confirmPassword"
            label="确认新密码"
            dependencies={['newPassword']}
            rules={[
              { required: true, message: '请确认新密码' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('两次输入的密码不一致'));
                },
              }),
            ]}
          >
            <Input.Password prefix={<LockOutlined />} placeholder="请确认新密码" />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={loading}>
                修改密码
              </Button>
              <Button onClick={() => passwordForm.resetFields()}>
                重置
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>

      <Card title="账户安全">
        <Space size="large" wrap>
          <span style={{ color: '#666' }}>Google验证器：</span>
          <Tag color={gaEnabled ? 'green' : 'default'}>
            {gaEnabled ? '已启用' : '未启用'}
          </Tag>
          {gaEnabled ? (
            <Button onClick={() => setGaDisableModalVisible(true)}>关闭</Button>
          ) : (
            <Button icon={<SafetyCertificateOutlined />} onClick={handleGaInit} loading={gaLoading}>
              开启
            </Button>
          )}
        </Space>
        <p style={{ color: '#999', fontSize: 12, marginTop: 12, marginBottom: 0 }}>
          启用后登录时除密码外还需输入 Google Authenticator 动态验证码。
        </p>
      </Card>

      <Modal
        title="开启Google验证器"
        open={gaModalVisible}
        onCancel={() => {
          setGaModalVisible(false);
          setGaInitData(null);
          setGaCode('');
        }}
        footer={[
          <Button key="cancel" onClick={() => {
            setGaModalVisible(false);
            setGaInitData(null);
            setGaCode('');
          }}>
            取消
          </Button>,
          <Button key="confirm" type="primary" loading={gaSubmitting} onClick={handleGaVerify}>
            确认启用
          </Button>
        ]}
      >
        {gaInitData && (
          <div style={{ textAlign: 'center', padding: '16px 0' }}>
            <p style={{ marginBottom: 12, color: '#666' }}>
              请使用 Google Authenticator 扫码，或手动输入密钥
            </p>
            <img
              src={gaInitData.qrDataUrl}
              alt="Google验证器二维码"
              style={{ width: 240, height: 240, marginBottom: 16 }}
            />
            <div style={{
              background: '#f5f5f5',
              padding: '10px',
              borderRadius: 4,
              fontFamily: 'monospace',
              wordBreak: 'break-all',
              marginBottom: 16
            }}>
              {gaInitData.secret}
            </div>
            <Input
              placeholder="输入6位动态验证码"
              maxLength={6}
              value={gaCode}
              onChange={(e) => setGaCode(e.target.value.replace(/\D/g, ''))}
              style={{ maxWidth: 220 }}
            />
          </div>
        )}
      </Modal>

      <Modal
        title="关闭Google验证器"
        open={gaDisableModalVisible}
        onCancel={() => {
          setGaDisableModalVisible(false);
          setGaDisableCode('');
        }}
        footer={[
          <Button key="cancel" onClick={() => {
            setGaDisableModalVisible(false);
            setGaDisableCode('');
          }}>
            取消
          </Button>,
          <Button key="confirm" type="primary" danger loading={gaSubmitting} onClick={handleGaDisable}>
            确认关闭
          </Button>
        ]}
      >
        <div style={{ padding: '16px 0' }}>
          <p style={{ marginBottom: 12, color: '#666' }}>
            请输入当前 Google Authenticator 动态验证码以确认操作
          </p>
          <Input
            placeholder="输入6位动态验证码"
            maxLength={6}
            value={gaDisableCode}
            onChange={(e) => setGaDisableCode(e.target.value.replace(/\D/g, ''))}
            style={{ maxWidth: 220 }}
          />
        </div>
      </Modal>
    </div>
  );
};

export default Profile;
